/**
 * The list table.
 *
 * Six of the M1 screens are "a filtered list of records", so the table is defined
 * once and configured with columns rather than rewritten per screen.
 *
 *   <DataTable
 *     rows={volunteers}
 *     getRowKey={(v) => v.volunteer_id}
 *     onRowClick={(v) => navigate(`/volunteers/${v.volunteer_id}`)}
 *     columns={[
 *       { key: 'code',   header: 'ID',     render: (v) => v.volunteer_code },
 *       { key: 'status', header: 'Status', render: (v) => <StatusBadge status={v.status} /> },
 *     ]}
 *   />
 *
 * It renders rows and nothing else — no fetching, no sorting, no pagination state.
 * Those belong to the screen, which owns the query.
 */

import { Fragment } from 'react';
import type { ReactNode } from 'react';
import styles from './DataTable.module.css';

export interface Column<Row> {
  /** Stable identity for the column. Also the React key. */
  key: string;
  header: ReactNode;
  render: (row: Row) => ReactNode;
  /** Right-align, for durations and counts. */
  numeric?: boolean;
  /** Centre the header and cells, for short counts. Ignored if `numeric` is set. */
  center?: boolean;
  /**
   * The name this column's value is given on a phone, where the table becomes
   * one card per row and the header row is gone (see the stylesheet).
   *
   * Defaults to `header` when that is a plain string, which it is for every
   * column in the application today. Set it when the header is a node, or
   * leave both empty for a column of controls — an unlabelled cell takes the
   * card's full width instead of sitting in the value column.
   */
  label?: string;
}

export interface DataTableProps<Row> {
  rows: Row[];
  columns: Column<Row>[];
  getRowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  /** Describes the table for screen readers, e.g. "Volunteers". */
  caption?: string;
  /**
   * A panel opened beneath a row, spanning every column. Return `null` for rows
   * that have nothing open, which is almost always all of them.
   *
   * Added in M2 for the attendance table, where the alternative had become
   * untenable: an audit trail and a correction form were both being rendered
   * *inside the actions cell*, which is a column sized for two buttons. The
   * table scrolled sideways to fit them, taking the date and the volunteer's
   * name off-screen — so the panel telling you what changed no longer showed you
   * whose record had changed.
   */
  renderExpanded?: (row: Row) => ReactNode;
}

function alignClass<Row>(column: Column<Row>): string | undefined {
  if (column.numeric) return styles.numeric;
  return column.center ? styles.center : undefined;
}

/** The stacked layout's name for a column, or undefined when it has none. */
function columnLabel<Row>(column: Column<Row>): string | undefined {
  const name = column.label ?? (typeof column.header === 'string' ? column.header : undefined);
  return name && name.trim() !== '' ? name : undefined;
}

export function DataTable<Row>({
  rows,
  columns,
  getRowKey,
  onRowClick,
  caption,
  renderExpanded,
}: DataTableProps<Row>) {
  const clickable = Boolean(onRowClick);

  return (
    <div className={styles.scroll}>
      <table className={styles.table}>
        {caption && <caption className="visually-hidden">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col" className={alignClass(column)}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const expanded = renderExpanded?.(row);

            return (
              <Fragment key={getRowKey(row)}>
                <tr
                  className={clickable ? styles.clickable : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // A clickable row must also be reachable and activatable by keyboard,
                  // or the whole list is closed to anyone not using a mouse.
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? 'button' : undefined}
                  onKeyDown={
                    onRowClick
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={alignClass(column)}
                      // Read back by `td::before` in the stacked phone layout.
                      // Absent rather than empty for a column of controls, so
                      // the stylesheet can tell "no name" from "a name".
                      data-label={columnLabel(column)}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>

                {expanded && (
                  <tr className={styles.expandedRow}>
                    {/*
                      Spans the whole table so the panel gets the page's width
                      rather than one column's, and sits directly under the row
                      it belongs to — which is what says whose record it is.
                    */}
                    <td colSpan={columns.length} className={styles.expanded}>
                      {/*
                        A table cell sizes to its content, so a wide panel widens
                        the whole table and scrolls the columns out of view — the
                        exact problem this row was added to solve. The wrapper's
                        `inline-size: 0; min-inline-size: 100%` is the standard
                        answer: it contributes no intrinsic width to the layout,
                        then fills whatever width the columns settle on.
                      */}
                      <div className={styles.expandedContent}>{expanded}</div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
